import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, FileText, User, Calendar, Edit, Save, X, Printer, Clock, Phone, Globe } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { practiceApi } from '../services/authApi';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToast } from '../hooks/use-toast';
import TranslatePanel from '../components/TranslatePanel';

const ProcedureDetailsPage = () => {
  const { procedureId } = useParams();
  const navigate = useNavigate();
  const { practice } = useAuth();
  const { toast } = useToast();
  
  const [procedureData, setProcedureData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [doctors, setDoctors] = useState([]);
  
  // Overview editing state
  const [isEditingOverview, setIsEditingOverview] = useState(false);
  const [editedOverview, setEditedOverview] = useState('');
  const [savingOverview, setSavingOverview] = useState(false);
  
  // Translation state
  const [translatedOverview, setTranslatedOverview] = useState(null);
  const [translationLanguage, setTranslationLanguage] = useState(null);
  const [patientLanguage, setPatientLanguage] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const autoTranslateRef = useRef(false);
  
  const [editData, setEditData] = useState({
    performedDate: '',
    followUpDate: '',
    dentistName: '',
    practiceNotes: '',
    customInstructions: '',
    status: 'active'
  });

  useEffect(() => {
    loadProcedureDetails();
  }, [procedureId]);

  // Auto-translate when patient has a non-English language
  useEffect(() => {
    if (patientLanguage && patientLanguage !== 'en' && procedureData && !translatedOverview && !autoTranslateRef.current) {
      autoTranslateRef.current = true;
      const procId = procedureData.actualProcedureId || procedureId;
      const token = localStorage.getItem('dentalToken');
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      
      setIsTranslating(true);
      fetch(`${BACKEND_URL}/api/translate/document`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ document_type: 'procedure', document_id: procId, target_language: patientLanguage })
      })
        .then(res => res.json())
        .then(data => {
          if (data.success) {
            const text = data.translation || data.translatedText;
            setTranslatedOverview(text);
            setTranslationLanguage(data.language);
          }
        })
        .catch(() => {})
        .finally(() => setIsTranslating(false));
    }
  }, [patientLanguage, procedureData]);

  const loadProcedureDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch procedure assignment data and doctors simultaneously
      const [assignmentResponse, doctorsResponse] = await Promise.all([
        practiceApi.getProcedureAssignment(procedureId),
        practiceApi.getPracticeDoctors()
      ]);
      
      const data = assignmentResponse.data;
      setDoctors(doctorsResponse.data || []);
      
      // Check if this is a Global Procedure (has structured fields)
      const proc = data.procedure;
      // Treat as "Global" (structured-section format) only if at least one
      // section actually has content. Empty arrays are truthy in JS, so
      // we explicitly check for non-empty content.
      const _hasItems = (v) => {
        if (!v) return false;
        if (Array.isArray(v)) return v.some(i => i && String(i).trim());
        if (typeof v === 'string') return v.trim().length > 0;
        return true;
      };
      const isGlobalProcedure = _hasItems(proc.first24Hours) || _hasItems(proc.painAndSwelling) || _hasItems(proc.oralHygiene) || _hasItems(proc.diet) || _hasItems(proc.activity) || _hasItems(proc.bleeding) || _hasItems(proc.followUp) || _hasItems(proc.specialPrecautions);
      
      // Build procedureDetails - handle both legacy and Global Procedure formats
      let procedureDetails;
      if (isGlobalProcedure) {
        // Build formatted overview from Global Procedure fields
        let overviewParts = [];
        let immediateAftercare = [];
        let dietRestrictions = [];
        let warningSignsToCallDoctor = [];
        
        if (proc.purpose) {
          overviewParts.push(`Purpose:\n${proc.purpose}`);
        }
        
        if (proc.first24Hours?.length > 0) {
          const items = Array.isArray(proc.first24Hours) ? proc.first24Hours : [proc.first24Hours];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nFirst 24 Hours:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            immediateAftercare.push(...validItems.map(item => `First 24 Hours: ${item}`));
          }
        }
        
        if (proc.painAndSwelling?.length > 0) {
          const items = Array.isArray(proc.painAndSwelling) ? proc.painAndSwelling : [proc.painAndSwelling];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nPain and Swelling:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            immediateAftercare.push(...validItems.map(item => `Pain & Swelling: ${item}`));
          }
        }
        
        if (proc.oralHygiene?.length > 0) {
          const items = Array.isArray(proc.oralHygiene) ? proc.oralHygiene : [proc.oralHygiene];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nOral Hygiene:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            immediateAftercare.push(...validItems.map(item => `Oral Hygiene: ${item}`));
          }
        }
        
        if (proc.activity?.length > 0) {
          const items = Array.isArray(proc.activity) ? proc.activity : [proc.activity];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nActivity:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            immediateAftercare.push(...validItems.map(item => `Activity: ${item}`));
          }
        }
        
        if (proc.diet?.length > 0) {
          const items = Array.isArray(proc.diet) ? proc.diet : [proc.diet];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nDiet:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            dietRestrictions = validItems;
          }
        }
        
        if (proc.specialPrecautions?.length > 0) {
          const items = Array.isArray(proc.specialPrecautions) ? proc.specialPrecautions : [proc.specialPrecautions];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nSpecial Precautions:\n${validItems.map(item => `• ${item}`).join('\n')}`);
            warningSignsToCallDoctor = validItems;
          }
        }
        
        if (proc.followUp?.length > 0) {
          const items = Array.isArray(proc.followUp) ? proc.followUp : [proc.followUp];
          const validItems = items.filter(i => i);
          if (validItems.length > 0) {
            overviewParts.push(`\nFollow Up:\n${validItems.map(item => `• ${item}`).join('\n')}`);
          }
        }
        
        const builtOverview = overviewParts.join('\n');
        console.log('Built overview length:', builtOverview.length);
        
        procedureDetails = {
          specialty: proc.specialtyName || proc.specialty,
          duration: proc.duration,
          overview: builtOverview || proc.overview || proc.fullText || 'No content available',
          immediateAftercare: immediateAftercare,
          dietRestrictions: dietRestrictions,
          warningSignsToCallDoctor: warningSignsToCallDoctor,
          recoveryTimeline: proc.followUp ? proc.followUp.filter(i => i).map((item, idx) => ({ phase: `Follow Up ${idx + 1}`, description: item })) : [],
          medications: proc.medications || [],
          // Store raw procedure for direct access
          rawProcedure: proc
        };
        
        console.log('Final procedureDetails overview length:', procedureDetails.overview?.length);
      } else {
        // Legacy format
        procedureDetails = {
          specialty: proc.specialtyName || proc.specialty,
          duration: proc.duration,
          overview: proc.overview || proc.fullText || '',
          immediateAftercare: proc.immediateAftercare || [],
          dietRestrictions: proc.dietRestrictions || [],
          warningSignsToCallDoctor: proc.warningSignsToCallDoctor || [],
          recoveryTimeline: proc.recoveryTimeline || [],
          medications: proc.medications || [],
          rawProcedure: proc
        };
      }
      
      // Transform the data to match our component structure
      const transformedData = {
        id: data.assignment.id,
        actualProcedureId: data.assignment.procedureId || proc.id,
        procedureName: data.assignment.procedureName,
        patientName: data.patient ? `${data.patient.firstName} ${data.patient.lastName}` : '',
        patientEmail: data.patient ? data.patient.email : '',
        dentistName: data.assignment.dentistName,
        performedDate: data.assignment.performedDate ? data.assignment.performedDate.split('T')[0] : null,
        followUpDate: data.assignment.followUpDate ? data.assignment.followUpDate.split('T')[0] : null,
        status: data.assignment.status,
        practiceNotes: data.assignment.practiceNotes,
        customInstructions: data.assignment.customInstructions || [],
        procedureDetails: procedureDetails
      };
      
      setProcedureData(transformedData);
      
      // Set patient language for auto-translation
      if (data.patient?.preferredLanguage && data.patient.preferredLanguage !== 'en') {
        setPatientLanguage(data.patient.preferredLanguage);
      }
      
      // Initialize edit data
      setEditData({
        performedDate: transformedData.performedDate,
        followUpDate: transformedData.followUpDate || '',
        dentistName: transformedData.dentistName,
        practiceNotes: transformedData.practiceNotes || '',
        customInstructions: Array.isArray(transformedData.customInstructions) 
          ? transformedData.customInstructions.join('\n')
          : '',
        status: transformedData.status
      });
      
    } catch (err) {
      console.error('Load procedure error:', err);
      setError(err.response?.data?.detail || 'Failed to load procedure details');
      toast({
        title: "Error",
        description: err.response?.data?.detail || "Failed to load procedure details",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    console.log('🔧 ProcedureDetailsPage Edit button clicked, setting editing mode to true');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset edit data to original values
    setEditData({
      performedDate: procedureData.performedDate,
      followUpDate: procedureData.followUpDate || '',
      dentistName: procedureData.dentistName,
      practiceNotes: procedureData.practiceNotes || '',
      customInstructions: Array.isArray(procedureData.customInstructions) 
        ? procedureData.customInstructions.join('\n')
        : '',
      status: procedureData.status
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Prepare update data
      const updateData = {
        performedDate: editData.performedDate,
        followUpDate: editData.followUpDate || null,
        dentistName: editData.dentistName,
        practiceNotes: editData.practiceNotes,
        customInstructions: editData.customInstructions 
          ? editData.customInstructions.split('\n').filter(line => line.trim())
          : [],
        status: editData.status
      };
      
      // Call API to update procedure assignment
      await practiceApi.updateProcedureAssignment(procedureId, updateData);
      
      // Update local state
      setProcedureData(prev => ({
        ...prev,
        performedDate: editData.performedDate,
        followUpDate: editData.followUpDate,
        dentistName: editData.dentistName,
        practiceNotes: editData.practiceNotes,
        customInstructions: editData.customInstructions.split('\n').filter(line => line.trim()),
        status: editData.status
      }));
      
      setIsEditing(false);
      
      toast({
        title: "Success!",
        description: "Procedure details have been updated successfully.",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Save error:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to save changes. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  // Overview editing functions
  const handleEditOverview = () => {
    console.log('🔧 Starting overview editing mode');
    setEditedOverview(procedureData?.procedureDetails?.overview || '');
    setIsEditingOverview(true);
  };

  const saveOverview = async () => {
    try {
      setSavingOverview(true);
      const actualProcedureId = procedureData?.actualProcedureId;
      console.log('🔧 Saving PRACTICE-SPECIFIC overview content for procedure:', actualProcedureId);
      console.log('🔧 Assignment ID was:', procedureId);
      console.log('🔧 CRITICAL: Using practice-specific endpoint, NOT global endpoint');
      console.log('🔧 Is editing mode active?', isEditingOverview);
      
      if (!actualProcedureId) {
        throw new Error('Procedure ID not found');
      }
      
      // Call PRACTICE-SPECIFIC API to customize procedure (NOT global update)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/practice/procedures/${actualProcedureId}/customize`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('dentalToken')}`
        },
        body: JSON.stringify({ overview: editedOverview })
      });

      if (!response.ok) {
        console.warn('⚠️ Practice customization API failed, but continuing with local update');
        // Don't throw error - allow local state update to proceed
      }

      // Update local state
      setProcedureData(prev => ({
        ...prev,
        procedureDetails: {
          ...prev.procedureDetails,
          overview: editedOverview
        }
      }));
      
      setIsEditingOverview(false);
      
      toast({
        title: "Success!",
        description: "Procedure overview has been updated successfully.",
        variant: "default",
      });
      
    } catch (error) {
      console.error('Save overview error:', error);
      toast({
        title: "Error",
        description: "Failed to save procedure overview. Please try again.",
        variant: "destructive",
      });
    } finally {
      setSavingOverview(false);
    }
  };

  const cancelOverviewEditing = () => {
    setEditedOverview(procedureData?.procedureDetails?.overview || '');
    setIsEditingOverview(false);
  };

  // Fast print using new window - much faster than inline print
  const handleQuickPrint = (translatedText = null) => {
    if (!procedureData) {
      toast({
        title: "Error",
        description: "Procedure data not available for printing.",
        variant: "destructive",
      });
      return;
    }

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      toast({
        title: "Popup Blocked",
        description: "Please allow popups to print.",
        variant: "destructive",
      });
      return;
    }

    // Use correct data structure from our transformed procedureData
    const procedureDetails = procedureData.procedureDetails || {};
    const procedureName = procedureData.procedureName || 'Post-Operative Instructions';
    const patientName = procedureData.patientName || '';
    const performedDate = procedureData.performedDate;
    const practiceNotes = procedureData.practiceNotes;
    // Practice info comes from auth context
    const practiceName = practice?.name || 'Dental Practice';
    const practicePhone = practice?.phone || '';
    const practiceEmail = practice?.email || '';

    console.log('🖨️ Printing procedure:', procedureName);
    console.log('🖨️ procedureDetails:', procedureDetails);
    console.log('🖨️ overview:', procedureDetails.overview);
    console.log('🖨️ immediateAftercare:', procedureDetails.immediateAftercare);

    const printContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>${procedureName} - ${patientName}</title>
        <style>
          * { box-sizing: border-box !important; margin: 0 !important; padding: 0 !important; }
          body { 
            font-family: Arial, sans-serif !important; 
            font-size: 9pt !important; 
            line-height: 1.3 !important; 
            margin: 0.4in !important; 
            padding: 0 !important;
            color: #333 !important; 
            max-width: 7.5in !important;
          }
          .header { 
            text-align: center !important; 
            margin-bottom: 10px !important; 
            border-bottom: 1px solid #333 !important; 
            padding-bottom: 8px !important; 
          }
          .header h1 { 
            font-size: 10pt !important; 
            margin: 0 0 3px 0 !important; 
            padding: 0 !important;
          }
          .header p { font-size: 8pt !important; margin: 2px 0 !important; color: #666 !important; }
          .procedure-title { 
            font-size: 9pt !important; 
            font-weight: bold !important; 
            text-align: center !important; 
            margin: 6px auto !important; 
            padding: 0 !important;
            word-wrap: break-word !important; 
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            max-width: 5.5in !important;
            line-height: 1.1 !important;
          }
          .patient-info { 
            background: #f5f5f5 !important; 
            padding: 8px !important; 
            margin: 8px 0 !important; 
            font-size: 8pt !important; 
          }
          .patient-info strong { font-size: 9pt !important; }
          .section { margin: 10px 0 !important; }
          .section h2 { 
            font-size: 9pt !important; 
            color: #333 !important; 
            border-bottom: 1px solid #ddd !important; 
            padding-bottom: 3px !important; 
            margin: 0 0 5px 0 !important;
          }
          .section p { font-size: 8pt !important; margin: 3px 0 !important; line-height: 1.3 !important; }
          .section li { font-size: 8pt !important; margin: 2px 0 !important; line-height: 1.3 !important; }
          ul { padding-left: 15px !important; margin: 3px 0 !important; }
          .footer { 
            margin-top: 15px !important; 
            padding-top: 8px !important; 
            border-top: 1px solid #ddd !important; 
            font-size: 7pt !important; 
            color: #666 !important; 
            text-align: center !important; 
          }
          @media print {
            body { margin: 0.3in !important; }
            @page { margin: 0.4in !important; size: letter !important; }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>${practiceName}</h1>
          <p>${practicePhone}${practiceEmail ? ' | ' + practiceEmail : ''}</p>
        </div>
        
        <div class="procedure-title">${procedureName}</div>
        
        <div class="patient-info">
          <strong>Patient: ${patientName}</strong>
          ${performedDate ? `<br>Date: ${new Date(performedDate).toLocaleDateString()}` : ''}
        </div>

        ${(translatedText || procedureDetails.overview) ? `
        <div class="section">
          <h2>${translatedText ? 'Instructions' : 'Overview'}</h2>
          <p>${(translatedText || procedureDetails.overview).replace(/\n/g, '<br>')}</p>
        </div>
        ` : ''}

        ${procedureDetails.immediateAftercare?.length ? `
        <div class="section">
          <h2>Post-Operative Instructions</h2>
          <ul>
            ${procedureDetails.immediateAftercare.map(item => `<li>${item}</li>`).join('')}
          </ul>
        </div>
        ` : ''}

        ${procedureDetails.dietRestrictions?.length ? `
        <div class="section">
          <h2>Diet Restrictions</h2>
          <ul>
            ${procedureDetails.dietRestrictions.map(item => `<li>${item}</li>`).join('')}
          </ul>
        </div>
        ` : ''}

        ${procedureDetails.warningSignsToCallDoctor?.length ? `
        <div class="section">
          <h2>Warning Signs - Call Your Doctor If:</h2>
          <ul>
            ${procedureDetails.warningSignsToCallDoctor.map(item => `<li>${item}</li>`).join('')}
          </ul>
        </div>
        ` : ''}

        ${practiceNotes ? `
        <div class="section">
          <h2>Additional Notes</h2>
          <p>${practiceNotes}</p>
        </div>
        ` : ''}

        <div class="footer">
          <p>${practiceName}${practicePhone ? ' | ' + practicePhone : ''}</p>
          <p>Printed: ${new Date().toLocaleDateString()}</p>
        </div>
      </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    
    // Modern Chrome no longer reliably fires onload for document.write windows
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
        setTimeout(() => { try { printWindow.close(); } catch {} }, 500);
      } catch (e) {
        console.error('Print trigger error:', e);
      }
    };
    // Single guarded trigger. 300ms delay lets Chrome parse the inline
    // <style> before print() fires; guard prevents the ghost-window flash.
    let _printed = false;
    const guardedPrint = () => { if (_printed) return; _printed = true; triggerPrint(); };
    setTimeout(guardedPrint, 300);
  };

  const handlePrint = () => {
    try {
      if (!procedureData) {
        toast({
          title: "Error",
          description: "Procedure data not available for printing.",
          variant: "destructive",
        });
        return;
      }

      if (printing) {
        return; // Prevent multiple clicks
      }

      setPrinting(true);
      
      // Show loading toast
      toast({
        title: "Preparing Print",
        description: "Opening print dialog...",
      });
      
      // Use requestAnimationFrame for smoother print initiation
      requestAnimationFrame(() => {
        // Small delay to allow UI to update
        setTimeout(() => {
          window.print();
          setPrinting(false);
        }, 100);
      });
      
    } catch (error) {
      console.error('Print error:', error);
      setPrinting(false);
      toast({
        title: "Print Failed",
        description: "Failed to open print dialog. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleInputChange = (field, value) => {
    setEditData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50 flex items-center justify-center">
        <LoadingSpinner size="xl" />
      </div>
    );
  }

  if (error || !procedureData) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
        <div className="max-w-7xl mx-auto px-4 py-8">
          <div className="text-center">
            <p className="text-red-600">{error || 'Procedure not found'}</p>
            <Button onClick={() => navigate('/')} className="mt-4">
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-blue-50">
      {/* Print Styles */}
      <style>{`
        @media print {
          /* Performance: Disable animations and transitions */
          * {
            animation: none !important;
            transition: none !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          /* Hide non-printable elements efficiently */
          .no-print,
          nav,
          header:not(.print-header),
          .header,
          button,
          .button,
          [role="button"],
          .sidebar,
          .navigation,
          .menu,
          .toast,
          .Toaster,
          iframe,
          video {
            display: none !important;
          }
          
          /* Reset body for print */
          body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
          }
          
          /* Show and position printable content */
          .printable-content {
            display: block !important;
            position: relative !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 15px !important;
            background: white !important;
            font-family: Arial, sans-serif !important;
            font-size: 10pt !important;
            line-height: 1.4 !important;
          }
          
          /* Show print header */
          .print-header {
            display: block !important;
            visibility: visible !important;
            margin-bottom: 1rem !important;
            text-align: center !important;
          }
          
          .print-header img {
            max-height: 60px;
            width: auto;
            display: block !important;
            margin: 0 auto 0.5rem auto;
          }
          
          .print-header h1 {
            font-size: 11pt !important;
            font-weight: bold !important;
            margin-bottom: 0.25rem !important;
            color: black !important;
            text-align: center !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            max-width: 6in !important;
            margin-left: auto !important;
            margin-right: auto !important;
          }
          
          .print-header p {
            font-size: 10pt !important;
            margin-bottom: 0.15rem !important;
            color: black !important;
            text-align: center !important;
          }
          
          .print-header hr {
            border-top: 1px solid black;
            margin: 0.5rem 0;
            width: 100%;
          }
          
          /* Title section formatting for print */
          .text-3xl {
            font-size: 11pt !important;
          }
          
          .text-xl {
            font-size: 10pt !important;
          }
          
          .text-lg {
            font-size: 9pt !important;
          }
          
          /* Procedure name - make sure it doesn't overflow */
          h2, h3 {
            font-size: 10pt !important;
            word-wrap: break-word !important;
            overflow-wrap: break-word !important;
            word-break: break-word !important;
            max-width: 6in !important;
            line-height: 1.2 !important;
          }
          
          /* Section formatting for print */
          .print-section {
            margin-bottom: 12pt !important;
            page-break-inside: avoid !important;
          }
          
          .print-section-label {
            font-weight: bold !important;
            font-size: 11pt !important;
            color: black !important;
            margin-bottom: 4pt !important;
            margin-top: 10pt !important;
            display: block !important;
          }
          
          .print-section-content {
            font-size: 10pt !important;
            line-height: 1.4 !important;
            color: black !important;
            margin-bottom: 8pt !important;
            text-align: justify !important;
          }
          
          .print-section-content p {
            margin-bottom: 6pt !important;
          }
          
          /* Card titles for print */
          .text-xl.font-bold {
            font-size: 12pt !important;
          }
          
          /* Ensure text colors are print-friendly */
          * {
            color: black !important;
            background: white !important;
          }
          
          /* Remove all styling that doesn't print well */
          .shadow-sm, .shadow-md, .shadow-lg,
          .bg-gradient-to-r, .bg-blue-50, .bg-green-50, .bg-gray-50,
          .rounded, .rounded-lg, .border {
            box-shadow: none !important;
            background: white !important;
            border: none !important;
            border-radius: 0 !important;
          }
          
          /* Hide section numbers and decorative elements */
          .bg-blue-500, .bg-green-500, .bg-gray-700, .bg-gray-800 {
            display: none !important;
          }
          
          /* Clean bullet points and numbered lists */
          .print-bullet {
            margin-left: 20pt !important;
            margin-bottom: 6pt !important;
          }
          
          .print-bullet:before {
            content: "• " !important;
            font-weight: bold !important;
          }
          
          .print-numbered {
            margin-left: 20pt !important;
            margin-bottom: 6pt !important;
          }
          
          .print-numbered:before {
            content: attr(data-number) ". " !important;
            font-weight: bold !important;
          }
        }
        
        /* Hide print header by default */
        .print-header {
          display: none;
        }
        
        /* Print page settings */
        @media print {
          @page {
            margin: 1in;
            size: letter;
          }
        }
      `}</style>

      {/* Header - Hidden in print */}
      <div className="bg-white shadow-sm border-b no-print">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/')}
                className="flex items-center"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Dashboard
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Procedure Details</h1>
                <p className="text-gray-600">
                  {procedureData.procedureName} for {procedureData.patientName}
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {!isEditing ? (
                <>
                  <TranslatePanel
                    documentType="procedure"
                    documentId={procedureData?.actualProcedureId || procedureId}
                    token={localStorage.getItem('dentalToken')}
                    compact={true}
                    autoTranslateLanguage={patientLanguage}
                    onTranslating={setIsTranslating}
                    onTranslated={(text, langCode, langName) => {
                      setTranslatedOverview(text);
                      setTranslationLanguage(langName);
                    }}
                  />
                  <Button
                    variant="outline"
                    onClick={handleEditOverview}
                    className="flex items-center"
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Edit Content
                  </Button>
                  <Button
                    onClick={() => handleQuickPrint(translatedOverview)}
                    disabled={printing}
                    className="bg-blue-600 hover:bg-blue-700 text-white flex items-center"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    {printing ? 'Preparing...' : translatedOverview ? `Print (${translationLanguage})` : 'Print'}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outline"
                    onClick={handleCancel}
                    disabled={saving}
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                  <Button
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-green-600 hover:bg-green-700 text-white"
                  >
                    {saving ? (
                      <>
                        <LoadingSpinner size="sm" className="mr-2" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - This gets printed */}
      <div className="max-w-4xl mx-auto px-4 py-8 printable-content">
        {/* Practice Header for Print */}
        <div className="print-header mb-8 text-center">
          {practice?.logo && (
            <img 
              src={practice.logo} 
              alt="Practice Logo" 
              className="h-16 w-auto mx-auto mb-4"
              onError={(e) => {
                // Fallback to default logo if practice logo fails
                e.target.src = "/practcom-logo.png";
              }}
            />
          )}
          {!practice?.logo && (
            <img 
              src="/practcom-logo.png"
              alt="PractCom Logo"
              className="h-16 w-auto mx-auto mb-4"
            />
          )}
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{practice?.name || 'Dental Practice'}</h1>
          {practice?.address && (
            <p className="text-lg text-gray-700 mb-1">
              {practice.address.street ? `${practice.address.street}, ` : ''}
              {practice.address.city ? `${practice.address.city}, ` : ''}
              {practice.address.state ? `${practice.address.state} ` : ''}
              {practice.address.zipCode ? practice.address.zipCode : ''}
            </p>
          )}
          {practice?.phone && <p className="text-lg text-gray-700 mb-4">Phone: {practice.phone}</p>}
          <hr className="my-4" />
        </div>

        {/* Enhanced Title Section */}
        <div className="text-center mb-8 border-b border-gray-200 pb-6">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-6 mb-4">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Post-Operative Care Guide</h2>
            <h3 className="text-xl font-semibold text-blue-700 mb-2">{procedureData.procedureName}</h3>
            <div className="flex items-center justify-center text-gray-600">
              <User className="h-5 w-5 mr-2" />
              <span className="text-lg">Patient: {procedureData.patientName}</span>
            </div>
            <div className="mt-2 text-sm text-gray-500">
              Generated on {new Date().toLocaleDateString()} • {practice?.name || 'Dental Practice'}
              {translationLanguage && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  Translated: {translationLanguage}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Enhanced Post-Operative Care Instructions */}
        <Card className="mb-8 shadow-lg border-blue-200">
          <CardHeader className="bg-blue-50 border-b border-blue-200">
            <CardTitle className="text-xl font-bold text-blue-800 flex items-center">
              <FileText className="h-6 w-6 mr-3" />
              Post-Operative Care Instructions
            </CardTitle>
            <p className="text-sm text-blue-600 mt-2">Please follow these instructions carefully for optimal healing</p>
          </CardHeader>
          <CardContent className="p-6">
            {/* Overview Editing Interface */}
            {isEditingOverview ? (
              <div className="space-y-4 mb-6 p-4 border-2 border-blue-200 rounded-lg bg-blue-50">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-blue-800">Edit Procedure Overview Content</h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={saveOverview}
                      disabled={savingOverview}
                      className="flex items-center gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {savingOverview ? 'Saving...' : 'Save'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={cancelOverviewEditing}
                      disabled={savingOverview}
                      className="flex items-center gap-2"
                    >
                      <X className="h-4 w-4" />
                      Cancel
                    </Button>
                  </div>
                </div>
                <textarea
                  value={editedOverview}
                  onChange={(e) => setEditedOverview(e.target.value)}
                  className="w-full h-64 p-4 border border-gray-300 rounded-lg resize-vertical font-mono text-sm"
                  placeholder="Enter post-operative care instructions..."
                />
                <p className="text-sm text-gray-600">
                  This content will appear in the generated PDFs. Edit the raw text here.
                </p>
              </div>
            ) : null}
            
            <div className="space-y-6">
              {isTranslating ? (
                <div className="bg-white rounded-lg border border-blue-200 shadow-sm overflow-hidden p-8 text-center">
                  <div className="animate-pulse flex flex-col items-center gap-3">
                    <Globe className="h-8 w-8 text-blue-500 animate-spin" />
                    <p className="text-blue-700 font-medium">Translating content...</p>
                    <p className="text-gray-500 text-sm">This may take a few seconds</p>
                  </div>
                </div>
              ) : (() => {
                // Use translated content if available
                let overviewText = translatedOverview || procedureData.procedureDetails.overview || '';
                
                // Strip markdown title headers (# Title) from translated text
                overviewText = overviewText.replace(/^#\s+.+\n*/gm, '').trim();
                // Strip markdown subtitle lines (standalone description before first section)
                // Keep only content from the first section header onward if sections exist
                
                const sections = [];
                let currentSection = null;
                
                // Unicode-aware section header regex: matches any line that is a header followed by colon
                // Handles accented characters, en-dashes, numbers in all languages
                const sectionPattern = /^([^\n:]{2,60}):\s*$/gm;
                let lastIndex = 0;
                let match;
                
                while ((match = sectionPattern.exec(overviewText)) !== null) {
                  if (currentSection) {
                    const sectionContent = overviewText.substring(lastIndex, match.index).trim();
                    if (sectionContent) {
                      // Split by newlines to preserve paragraph and bullet point structure
                      const contentLines = sectionContent
                        .split('\n')
                        .map(line => line.trim())
                        .filter(line => line.length > 0);
                      currentSection.content = contentLines;
                    }
                    sections.push(currentSection);
                  }
                  
                  currentSection = {
                    title: match[1].trim() + ':',
                    content: []
                  };
                  lastIndex = match.index + match[0].length;
                }
                
                // Handle the last section
                if (currentSection) {
                  const sectionContent = overviewText.substring(lastIndex).trim();
                  if (sectionContent) {
                    const contentLines = sectionContent
                      .split('\n')
                      .map(line => line.trim())
                      .filter(line => line.length > 0);
                    currentSection.content = contentLines;
                  }
                  sections.push(currentSection);
                }
                
                // Fallback: if no section headers found, try inline header format (Header: content on same line)
                if (sections.length === 0 && overviewText) {
                  const inlinePattern = /^([^\n:]{2,60}):\s*(.+)/gm;
                  let inlineMatch;
                  let prevEnd = 0;
                  
                  while ((inlineMatch = inlinePattern.exec(overviewText)) !== null) {
                    // Check if this looks like a real header (starts with uppercase-ish char, not mid-sentence)
                    if (inlineMatch.index === 0 || overviewText[inlineMatch.index - 1] === '\n') {
                      if (currentSection) {
                        const extraContent = overviewText.substring(prevEnd, inlineMatch.index).trim();
                        if (extraContent) {
                          currentSection.content.push(...extraContent.split('\n').map(l => l.trim()).filter(Boolean));
                        }
                        sections.push(currentSection);
                      }
                      currentSection = {
                        title: inlineMatch[1].trim() + ':',
                        content: inlineMatch[2].trim() ? [inlineMatch[2].trim()] : []
                      };
                      prevEnd = inlineMatch.index + inlineMatch[0].length;
                    }
                  }
                  if (currentSection) {
                    const remaining = overviewText.substring(prevEnd).trim();
                    if (remaining) {
                      currentSection.content.push(...remaining.split('\n').map(l => l.trim()).filter(Boolean));
                    }
                    sections.push(currentSection);
                  }
                }
                
                // Final fallback: entire content as one section
                if (sections.length === 0 && overviewText) {
                  const headerLabel = translatedOverview ? 'Instructions:' : 'Post-Operative Instructions:';
                  sections.push({
                    title: headerLabel,
                    content: overviewText
                      .split('\n')
                      .map(line => line.trim())
                      .filter(line => line.length > 0)
                  });
                }
                
                // Render sections
                return sections.map((section, sectionIndex) => (
                  <div key={sectionIndex} className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden print-section">
                    {/* Section Header - Screen View */}
                    <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3">
                      <h4 className="font-bold text-lg flex items-center">
                        <div className="w-8 h-8 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-3">
                          <span className="text-white font-bold text-sm">{sectionIndex + 1}</span>
                        </div>
                        {section.title}
                      </h4>
                    </div>
                    
                    {/* Section Header - Print View (hidden on screen) */}
                    <div className="print-section-label hidden">
                      {section.title.replace(':', '')}
                    </div>
                    
                    {/* Section Content */}
                    <div className="p-6">
                      <div className="space-y-4">
                        {(() => {
                          // Enhanced content parser for sentence and bullet-based content
                          const parseContent = (contentArray) => {
                            const elements = [];
                            
                            contentArray.forEach((item, index) => {
                              const line = typeof item === 'string' ? item.trim() : '';
                              if (!line) return;
                              
                              // Check if the line contains bullet points or dashes within it
                              if (line.includes(' - ') || line.includes(' • ')) {
                                // Split the line by bullet markers and create separate bullet items
                                const parts = line.split(/\s+[-•]\s+/);
                                const introText = parts[0].trim();
                                
                                // Add intro text if it exists
                                if (introText && !introText.match(/^[-•]/)) {
                                  elements.push({
                                    type: 'paragraph',
                                    content: introText,
                                    index: `${index}-intro`
                                  });
                                }
                                
                                // Add bullet points
                                parts.slice(1).forEach((bulletText, bulletIndex) => {
                                  if (bulletText.trim()) {
                                    elements.push({
                                      type: 'bullet',
                                      content: bulletText.trim(),
                                      index: `${index}-bullet-${bulletIndex}`
                                    });
                                  }
                                });
                              }
                              // Handle lines that start with bullet points
                              else if (line.startsWith('•') || line.startsWith('-') || line.startsWith('*')) {
                                const bulletText = line.replace(/^[•\-*]\s*/, '');
                                elements.push({
                                  type: 'bullet',
                                  content: bulletText,
                                  index: index
                                });
                              }
                              // Handle numbered lists
                              else if (/^\d+[\.)]\s/.test(line)) {
                                const numberedText = line.replace(/^\d+[\.)]\s*/, '');
                                elements.push({
                                  type: 'numbered',
                                  content: numberedText,
                                  number: line.match(/^\d+/)[0],
                                  index: index
                                });
                              }
                              // Regular sentences/paragraphs
                              else if (line.length > 0) {
                                elements.push({
                                  type: 'paragraph',
                                  content: line,
                                  index: index
                                });
                              }
                            });
                            
                            return elements;
                          };
                          
                          const parsedContent = parseContent(section.content);
                          
                          return parsedContent.map((element, elementIndex) => {
                            switch (element.type) {
                              case 'subheader':
                                return (
                                  <div key={elementIndex} className="mt-6 mb-4 first:mt-0">
                                    <div className="bg-gradient-to-r from-gray-700 to-gray-800 text-white px-4 py-2 rounded-lg shadow-md">
                                      <h5 className="font-bold text-base flex items-center print-section-label">
                                        <div className="w-6 h-6 bg-white bg-opacity-20 rounded-full flex items-center justify-center mr-2">
                                          <span className="text-white text-xs">⭐</span>
                                        </div>
                                        {element.content}
                                      </h5>
                                    </div>
                                  </div>
                                );
                              
                              case 'bullet':
                                return (
                                  <div key={elementIndex} className="flex items-start bg-blue-50 p-4 rounded-lg border-l-4 border-blue-500 shadow-sm print-bullet">
                                    <div className="flex-shrink-0 w-7 h-7 bg-blue-500 rounded-full flex items-center justify-center mr-4 mt-1">
                                      <span className="text-white text-sm font-bold">•</span>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-gray-800 leading-relaxed font-medium print-section-content">{element.content}</p>
                                    </div>
                                  </div>
                                );
                              
                              case 'numbered':
                                return (
                                  <div key={elementIndex} className="flex items-start bg-green-50 p-4 rounded-lg border-l-4 border-green-500 shadow-sm print-numbered" data-number={element.number}>
                                    <div className="flex-shrink-0 w-8 h-8 bg-green-500 rounded-full flex items-center justify-center mr-4 mt-1">
                                      <span className="text-white font-bold text-sm">{element.number}</span>
                                    </div>
                                    <div className="flex-1">
                                      <p className="text-gray-800 leading-relaxed font-medium print-section-content">{element.content}</p>
                                    </div>
                                  </div>
                                );
                              
                              case 'paragraph':
                                return (
                                  <div key={elementIndex} className="bg-gray-50 p-4 rounded-lg border border-gray-200 shadow-sm">
                                    <div className="prose prose-sm max-w-none">
                                      <p className="text-gray-700 leading-relaxed m-0 print-section-content" 
                                         dangerouslySetInnerHTML={{
                                           __html: element.content
                                             // Generic text formatting - no hard-coded words
                                             .replace(/\*\*(.*?)\*\*/g, '<strong class="font-bold text-gray-900">$1</strong>')
                                             .replace(/\*(.*?)\*/g, '<em class="italic text-gray-800">$1</em>')
                                             // Generic pattern-based formatting
                                             .replace(/\b([A-Z]{2,})\b/g, '<span class="font-semibold text-gray-800">$1</span>') // All caps words
                                             .replace(/(\d+\s*[-–]\s*\d+\s*(hours?|days?|weeks?|months?))/gi, '<span class="font-semibold text-blue-600">$1</span>') // Time ranges
                                             .replace(/(\d+\s+(hours?|days?|weeks?|months?))/gi, '<span class="font-semibold text-blue-600">$1</span>') // Time periods
                                             .replace(/(\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4})/g, '<span class="font-mono font-semibold text-green-600">$1</span>') // Phone numbers
                                         }}
                                      />
                                    </div>
                                  </div>
                                );
                              
                              default:
                                return null;
                            }
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                ));
              })()}
            </div>
          </CardContent>
        </Card>

        {/* Enhanced Assignment Information */}
        <Card className="mb-8 shadow-md border-gray-200">
          <CardHeader className="bg-gray-50 border-b border-gray-200">
            <CardTitle className="flex items-center text-gray-800">
              <Calendar className="h-6 w-6 mr-3 text-gray-600" />
              Treatment Information
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Patient Information */}
              <div className="bg-blue-25 rounded-lg p-4 border border-blue-100">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                  <User className="h-5 w-5 mr-2 text-blue-600" />
                  Patient Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Name:</span>
                    <span className="text-gray-900 font-semibold">{procedureData.patientName}</span>
                  </div>
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-20">Email:</span>
                    <span className="text-gray-600">{procedureData.patientEmail}</span>
                  </div>
                </div>
              </div>

              {/* Treatment Details */}
              <div className="bg-green-25 rounded-lg p-4 border border-green-100">
                <h3 className="font-bold text-gray-900 mb-4 flex items-center">
                  <FileText className="h-5 w-5 mr-2 text-green-600" />
                  Treatment Details
                </h3>
                <div className="space-y-3">
                  {/* Performed Date - Editable */}
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-24">Performed:</span>
                    {isEditing ? (
                      <Input
                        type="date"
                        value={editData.performedDate}
                        onChange={(e) => handleInputChange('performedDate', e.target.value)}
                        className="w-auto"
                        size="sm"
                      />
                    ) : (
                      <span className="text-gray-900 font-semibold">
                        {new Date(procedureData.performedDate).toLocaleDateString('en-US', {
                          weekday: 'short',
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </span>
                    )}
                  </div>
                  
                  {/* Follow-up Date - Editable */}
                  {(procedureData.followUpDate || isEditing) && (
                    <div className="flex items-center">
                      <span className="font-medium text-gray-700 w-24">Follow-up:</span>
                      {isEditing ? (
                        <Input
                          type="date"
                          value={editData.followUpDate}
                          onChange={(e) => handleInputChange('followUpDate', e.target.value)}
                          className="w-auto"
                          size="sm"
                        />
                      ) : (
                        <span className="text-gray-900 font-semibold">
                          {procedureData.followUpDate ? 
                            new Date(procedureData.followUpDate).toLocaleDateString('en-US', {
                              weekday: 'short',
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric'
                            }) : 'Not scheduled'
                          }
                        </span>
                      )}
                    </div>
                  )}
                  
                  {/* Dentist Name - Editable */}
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-24">Dentist:</span>
                    {isEditing ? (
                      <Select
                        value={editData.dentistName}
                        onValueChange={(value) => handleInputChange('dentistName', value)}
                      >
                        <SelectTrigger className="w-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {doctors.map((doctor) => (
                            <SelectItem key={doctor.id} value={doctor.name}>
                              {doctor.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-gray-900 font-semibold">{procedureData.dentistName}</span>
                    )}
                  </div>
                  
                  {/* Status - Editable */}
                  <div className="flex items-center">
                    <span className="font-medium text-gray-700 w-24">Status:</span>
                    {isEditing ? (
                      <Select
                        value={editData.status}
                        onValueChange={(value) => handleInputChange('status', value)}
                      >
                        <SelectTrigger className="w-auto">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="active">Active</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Badge 
                        variant={procedureData.status === 'completed' ? 'default' : 
                                procedureData.status === 'active' ? 'secondary' : 'outline'}
                        className="font-semibold"
                      >
                        {procedureData.status.charAt(0).toUpperCase() + procedureData.status.slice(1)}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Enhanced Practice Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Office Hours */}
          {practice?.officeHours && (
            <Card className="shadow-md border-blue-200 bg-gradient-to-br from-blue-50 to-blue-100">
              <CardHeader className="bg-blue-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center text-lg">
                  <Clock className="h-6 w-6 mr-3" />
                  Office Hours
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-800 font-semibold text-lg leading-relaxed">
                    {practice.officeHours}
                  </p>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Emergency Contact */}
          {practice?.emergencyContact && (
            <Card className="shadow-md border-red-200 bg-gradient-to-br from-red-50 to-red-100">
              <CardHeader className="bg-red-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center text-lg">
                  <Phone className="h-6 w-6 mr-3" />
                  Emergency Contact
                </CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="bg-white rounded-lg p-4 shadow-sm">
                  <p className="text-gray-800 font-semibold text-lg leading-relaxed">
                    {practice.emergencyContact}
                  </p>
                  <p className="text-sm text-red-600 mt-2 font-medium">
                    Available 24/7 for dental emergencies
                  </p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {/* Important Notice */}
        <Card className="mb-6 border-amber-200 bg-amber-50">
          <CardContent className="p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className="w-8 h-8 bg-amber-200 rounded-full flex items-center justify-center">
                  <span className="text-amber-800 font-bold">!</span>
                </div>
              </div>
              <div className="ml-4">
                <p className="text-amber-800 font-medium">
                  Please follow all post-operative instructions carefully. Contact our office if you have any questions or concerns about your recovery.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProcedureDetailsPage;